import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import suppliersService, { Supplier } from '../../../services/supplierCrm/suppliersService';
import supplierSolutionsService, {
  CatalogItem,
  CatalogItemInput,
  MarginType
} from '../../../services/supplierCrm/supplierSolutionsService';
import { useSupplierSolutionsStore } from '../../../store/supplierSolutions.store';
import { inventoryService, type InventoryItem } from '../../../services/supplierCrm/inventoryService';

const DEFAULT_MARGIN_TYPE: MarginType = 'percent';

const SolutionsTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  
  const {
    catalog,
    loadingCatalog,
    saving,
    fetchCatalog,
    createCatalogItem,
    deleteCatalogItem,
  } = useSupplierSolutionsStore();

  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [togglingInventory, setTogglingInventory] = useState<Record<string, boolean>>({});
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formSupplierId, setFormSupplierId] = useState('');
  const [formCost, setFormCost] = useState('');
  const [formMarginType, setFormMarginType] = useState<MarginType>(DEFAULT_MARGIN_TYPE);
  const [formMarginValue, setFormMarginValue] = useState('');
  const [formDescription, setFormDescription] = useState('');

  useEffect(() => {
    if (!activeOrgId) return;
    void fetchCatalog({ organization_id: activeOrgId, limit: 100, offset: 0 });
    
    void suppliersService.list({ organization_id: activeOrgId, limit: 100, offset: 0 })
      .then(res => setSupplierOptions(res.data || []))
      .catch(err => console.error('Failed to load suppliers:', err));
    
    // Load inventory items
    void inventoryService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 })
      .then(res => setInventoryItems(res.data || []))
      .catch(err => console.error('Failed to load inventory:', err));
  }, [activeOrgId, fetchCatalog]);

  const resetForm = useCallback(() => {
    setFormName('');
    setFormSupplierId('');
    setFormCost('');
    setFormMarginType(DEFAULT_MARGIN_TYPE);
    setFormMarginValue('');
    setFormDescription('');
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId) return;

    const payload: CatalogItemInput = {
      organization_id: activeOrgId,
      supplier_id: formSupplierId,
      name: formName.trim(),
      base_cost: parseFloat(formCost),
      default_margin_type: formMarginType,
      default_margin_value: parseFloat(formMarginValue),
      description: formDescription.trim() || undefined
    };

    try {
      await createCatalogItem(payload);
      toast.success('Catalog item added');
      resetForm();
      setShowAddForm(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add catalog item');
    }
  }, [activeOrgId, formName, formSupplierId, formCost, formMarginType, formMarginValue, formDescription, createCatalogItem, resetForm]);

  const handleDelete = useCallback(async (id: string) => {
    if (!activeOrgId || !window.confirm('Delete this catalog item?')) return;
    
    try {
      await deleteCatalogItem(id, activeOrgId);
      toast.success('Catalog item deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete item');
    }
  }, [activeOrgId, deleteCatalogItem]);

  const calculatePrice = (cost: number, marginType: MarginType, marginValue: number) => {
    if (marginType === 'percent') {
      return cost * (1 + marginValue / 100);
    }
    // 'absolute' type adds fixed amount
    return cost + marginValue;
  };

  const handleToggleInventoryTracking = async (catalogItem: CatalogItem, isCurrentlyTracked: boolean) => {
    if (!activeOrgId) return;
    
    setTogglingInventory(prev => ({ ...prev, [catalogItem.id]: true }));
    
    try {
      if (isCurrentlyTracked) {
        // Find and potentially delete inventory item
        const invItem = inventoryItems.find(i => i.catalog_item_id === catalogItem.id);
        if (invItem) {
          if (!window.confirm('Remove inventory tracking for this item? This will not delete transaction history.')) {
            setTogglingInventory(prev => ({ ...prev, [catalogItem.id]: false }));
            return;
          }
          await inventoryService.delete(invItem.id, activeOrgId);
          setInventoryItems(prev => prev.filter(i => i.id !== invItem.id));
          toast.success('Inventory tracking disabled');
        }
      } else {
        // Create new inventory item
        const { data, error } = await inventoryService.create({
          organization_id: activeOrgId,
          catalog_item_id: catalogItem.id,
          supplier_id: catalogItem.supplier_id,
          sku: `CAT-${catalogItem.id.substring(0, 8)}`,
          current_stock: 0,
          reorder_point: 10,
          reorder_quantity: 50,
          location: 'Main Warehouse',
          unit_of_measure: 'unit',
          notes: `Auto-created from catalog item: ${catalogItem.name}`
        });
        
        if (error) {
          toast.error(error);
        } else if (data) {
          setInventoryItems(prev => [...prev, data]);
          toast.success('Inventory tracking enabled');
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to toggle inventory tracking');
    } finally {
      setTogglingInventory(prev => ({ ...prev, [catalogItem.id]: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Product Catalog</h3>
          <p className="text-sm text-gray-600">Manage supplier products with locked margins</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          {showAddForm ? 'Cancel' : '➕ Add Product'}
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Product Name *</label>
              <input
                required
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Widget Pro"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Supplier *</label>
              <select
                required
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formSupplierId}
                onChange={(e) => setFormSupplierId(e.target.value)}
              >
                <option value="">Select supplier</option>
                {supplierOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Cost (Base Price) *</label>
              <input
                required
                type="number"
                step="0.01"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formCost}
                onChange={(e) => setFormCost(e.target.value)}
                placeholder="100.00"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">Margin Type *</label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formMarginType}
                onChange={(e) => setFormMarginType(e.target.value as MarginType)}
              >
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Margin Value * {formMarginType === 'percent' ? '(%)' : '($)'}
              </label>
              <input
                required
                type="number"
                step="0.01"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formMarginValue}
                onChange={(e) => setFormMarginValue(e.target.value)}
                placeholder={formMarginType === 'percent' ? '20' : '50.00'}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-gray-500">Description</label>
              <textarea
                rows={2}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); resetForm(); }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Adding...' : 'Add to Catalog'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Catalog Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {loadingCatalog ? (
          <div className="md:col-span-3 rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-100">
            <div className="text-gray-500">Loading catalog...</div>
          </div>
        ) : catalog.length === 0 ? (
          <div className="md:col-span-3 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
            <div className="text-4xl mb-4">📦</div>
            <h3 className="text-lg font-semibold text-gray-900">No Products Yet</h3>
            <p className="mt-2 text-sm text-gray-600">
              Add products from your suppliers to build your catalog
            </p>
          </div>
        ) : (
          catalog.map((item) => {
            const supplier = supplierOptions.find(s => s.id === item.supplier_id);
            const sellPrice = calculatePrice(item.base_cost, item.default_margin_type, item.default_margin_value);
            const invItem = inventoryItems.find(i => i.catalog_item_id === item.id);
            const isTracked = !!invItem;
            const isToggling = togglingInventory[item.id] || false;
            
            return (
              <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-3">
                  <h4 className="text-lg font-semibold text-gray-900">{item.name}</h4>
                  {supplier && (
                    <p className="text-sm text-gray-500">from {supplier.name}</p>
                  )}
                </div>
                
                {item.description && (
                  <p className="mb-3 text-sm text-gray-600 line-clamp-2">{item.description}</p>
                )}
                
                <div className="mb-4 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost:</span>
                    <span className="font-medium">${item.base_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Margin:</span>
                    <span className="font-medium">
                      {item.default_margin_type === 'percent' ? `${item.default_margin_value}%` : `$${item.default_margin_value}`}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-1">
                    <span className="font-semibold text-gray-900">Sell Price:</span>
                    <span className="font-bold text-green-600">${sellPrice.toFixed(2)}</span>
                  </div>
                </div>

                {/* Inventory Tracking Toggle */}
                <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">📦 Track Inventory</span>
                      {isTracked && invItem && (
                        <span className={`text-xs font-semibold ${
                          invItem.current_stock <= invItem.reorder_point ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {invItem.current_stock} {invItem.unit_of_measure}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleToggleInventoryTracking(item, isTracked)}
                      disabled={isToggling}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isTracked ? 'bg-primary' : 'bg-gray-300'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isTracked ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {isTracked && invItem && (
                    <div className="mt-2 text-xs text-gray-600">
                      Location: {invItem.location} | Reorder at: {invItem.reorder_point}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleDelete(item.id)}
                  className="w-full rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                  disabled={saving}
                >
                  Delete
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SolutionsTab;
